import {
  BBox,
  Feature,
  FeatureCollection,
  Geometry,
  Position
} from "geojson";
import proj4, { InterfaceCoordinates } from "proj4";

type Coord = Position;
type Line = Coord[];
type MultiLine = Line[];
type MultiPoly = MultiLine[];

proj4.defs('WGS84', "+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees");


/**
 * Transform a single coordinate
 */
const transformPoint = (
  coord: Coord,
  transform: (c: InterfaceCoordinates) => InterfaceCoordinates
): Coord => {
  // @ts-ignore
  const [x, y] = transform(coord);
  return [x, y];
};

/**
 * Transform a geometry's bbox
 */
const transformBBox = (
  bbox: BBox,
  transform: (c: InterfaceCoordinates) => InterfaceCoordinates
): number[] => {
  const ll = transformPoint(bbox.slice(0, 2), transform);
  const ur = transformPoint(bbox.slice(2) as Coord, transform);
  return [...ll, ...ur];
};

/**
 * Recursively transform a GeoJSON geometry
 */
const transformGeometry = (
  geometry: Geometry,
  transform: (c: InterfaceCoordinates) => InterfaceCoordinates
): Geometry => {
  const base = { ...geometry } as any;

  switch (geometry.type) {
    case "Point":
      return {
        ...base,
        coordinates: transformPoint(geometry.coordinates, transform)
      };

    case "MultiPoint":
    case "LineString":
      return {
        ...base,
        coordinates: geometry.coordinates.map(c => transformPoint(c, transform))
      };

    case "MultiLineString":
    case "Polygon":
      return {
        ...base,
        coordinates: geometry.coordinates.map(
          line => line.map(c => transformPoint(c, transform))
        )
      };

    case "MultiPolygon":
      return {
        ...base,
        coordinates: geometry.coordinates.map(poly =>
          poly.map(line => line.map(c => transformPoint(c, transform)))
        )
      };

    case "GeometryCollection":
      return {
        ...base,
        geometries: geometry.geometries.map(g => transformGeometry(g, transform))
      };

    default:
      return geometry;
  }
};

/**
 * Transform a GeoJSON Feature
 */
const transformFeature = (
  feature: Feature,
  transform: (c: InterfaceCoordinates) => InterfaceCoordinates
): Feature => {
  const { geometry, ...rest } = feature;
  return {
    ...rest,
    geometry: transformGeometry(geometry, transform),
    // @ts-ignore
    bbox: feature.bbox ? transformBBox(feature.bbox, transform) : undefined
  };
};

/**
 * Transform a GeoJSON FeatureCollection
 */
export const transformFeatureCollection = (
  fc: FeatureCollection,
  fromProj: string,
  toProj: string = "EPSG:4326"
): FeatureCollection => {
  const proj = proj4(fromProj, toProj);

  return {
    ...fc,
    features: fc.features.map(f => transformFeature(f, proj.forward)),
    // @ts-ignore
    bbox: fc.bbox ? transformBBox(fc.bbox, proj.forward) : undefined
  };
};

export const toWGS84 = (
  fc: FeatureCollection,
  sourceProjection: string
): FeatureCollection => {
  return transformFeatureCollection(fc, sourceProjection, "EPSG:4326");
}